import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
} from '@mui/material';

import Card from '@/components/Card';
import Button from '@/components/Button';
import Stack from '@/components/Stack';
import { fetchRegion, updateRegion, deleteRegion } from './api';
import type { RegionDetail as RegionDetailType } from './types';
import ZipcodeManager from './ZipcodeManager';

type Props = {
  regionId: number;
  onBack: () => void;
  onUpdated: () => void;
};

const RegionDetail = ({ regionId, onBack, onUpdated }: Props) => {
  const [region, setRegion] = useState<RegionDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [jobTaxType, setJobTaxType] = useState('');
  const [isActive, setIsActive] = useState(true);

  const loadRegion = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchRegion(regionId);
      setRegion(response.data);
      setName(response.data.name);
      setJobTaxType(response.data.job_tax_type);
      setIsActive(response.data.is_active);
    } catch (err: any) {
      setError(err.message || 'Failed to load region');
    } finally {
      setLoading(false);
    }
  }, [regionId]);

  useEffect(() => {
    loadRegion();
  }, [loadRegion]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const response = await updateRegion(regionId, {
        name: name.trim(),
        job_tax_type: jobTaxType,
        is_active: isActive,
      });
      setRegion({ ...region!, ...response.data });
      setSuccessMsg('Region updated');
      onUpdated();
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to update region',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Are you sure you want to deactivate this region?')) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await deleteRegion(regionId);
      setSuccessMsg(
        `Region deactivated. ${response.data.affected_workers} workers affected.`,
      );
      setIsActive(false);
      onUpdated();
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate region');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (!region) {
    return <Alert severity="error">{error || 'Region not found'}</Alert>;
  }

  return (
    <Box>
      <Stack align="center" justify="apart">
        <Button
          a11yLabel="Back to regions"
          label="Back to Regions"
          appearance="plain"
          onClick={onBack}
        />
        <Typography variant="caption" color="text.secondary">
          State: {region.state} (cannot be changed)
        </Typography>
      </Stack>

      <Card>
        <Card.Section>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {successMsg && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMsg}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              label="Region Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Tax Type</InputLabel>
              <Select
                value={jobTaxType}
                label="Tax Type"
                onChange={(e) => setJobTaxType(e.target.value)}
              >
                <MenuItem value="w2">W2</MenuItem>
                <MenuItem value="1099">1099</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label="Active"
            />
          </Box>

          <Stack justify="apart">
            <Button
              a11yLabel="Save changes"
              label="Save Changes"
              isLoading={saving}
              onClick={handleSave}
            />
            {isActive && (
              <Button
                a11yLabel="Deactivate region"
                label="Deactivate"
                status="danger"
                isLoading={saving}
                onClick={handleDeactivate}
              />
            )}
          </Stack>
        </Card.Section>
      </Card>

      <Divider sx={{ my: 3 }} />

      <ZipcodeManager
        regionId={regionId}
        regionState={region.state}
        onZipcodesChanged={loadRegion}
      />
    </Box>
  );
};

export default RegionDetail;
